import React from "react";
import { FiX, FiCheck, FiAlertCircle, FiDownload, FiCheckCircle, FiXCircle } from "react-icons/fi";

const BulkIDGenerationProgressModal = ({
  isOpen,
  onClose,
  currentStep,
  totalSteps,
  stepName,
  progress,
  completedSteps,
  error,
  onDownloadPDF,
  onRetry,
  isDownloading,
  title = "Generating Class ID Cards",
  subtitle = "Please wait while we process your request",
  successTitle = "ID Cards Generated Successfully!",
  successSubtitle = "Your ID cards are ready for download",
  results = null,
}) => {
  if (!isOpen) return null;

  const getStepIcon = (stepIndex) => {
    if (completedSteps.includes(stepIndex)) {
      return <FiCheck className="w-5 h-5 text-green-600" />;
    } else if (stepIndex === currentStep) {
      return (
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      );
    } else {
      return <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />;
    }
  };

  const getStepStatus = (stepIndex) => {
    if (completedSteps.includes(stepIndex)) {
      return "text-green-600";
    } else if (stepIndex === currentStep) {
      return "text-blue-600";
    } else {
      return "text-gray-500";
    }
  };

  const steps = [
    "Generating QR Codes",
    "Creating ID Card Templates",
    "Generating PDF",
    "Ready for Download",
  ];

  const successfulCount = results ? results.filter((r) => r.success).length : null;
  const failedCount = results ? results.filter((r) => !r.success).length : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            </div>
            {!isDownloading && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
              >
                <FiX className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Progress Steps */}
          <div className="space-y-4 mb-6">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center space-x-3">
                {getStepIcon(index)}
                <span className={`text-sm font-medium ${getStepStatus(index)}`}>{step}</span>
                {index === currentStep && (
                  <span className="text-xs text-gray-500 ml-auto">{progress}%</span>
                )}
              </div>
            ))}
          </div>

          {/* Current Step Progress Bar */}
          {currentStep < steps.length && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">{stepName}</span>
                <span className="text-sm text-gray-500">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="mt-3 px-4 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {/* Success State */}
          {completedSteps.includes(3) && !error && (
            <div>
              <div className="text-center mb-4">
                <FiCheck className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-800 mb-1">{successTitle}</h3>
                <p className="text-sm text-gray-600">{successSubtitle}</p>
              </div>

              {/* Itemized Results Summary */}
              {results && results.length > 0 && (
                <div className="mb-6 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-600 px-2 pb-2 border-b border-slate-200">
                    <span>Summary ({results.length} total)</span>
                    <span className="space-x-2">
                      <span className="text-green-600">✓ {successfulCount} succeeded</span>
                      {failedCount > 0 && (
                        <span className="text-red-600">✗ {failedCount} failed</span>
                      )}
                    </span>
                  </div>
                  <div className="max-h-44 overflow-y-auto divide-y divide-slate-100 text-xs mt-1">
                    {results.map((res, idx) => (
                      <div key={idx} className="py-1.5 px-2 flex items-center justify-between">
                        <div className="flex items-center space-x-2 truncate">
                          {res.success ? (
                            <FiCheckCircle className="text-green-500 flex-shrink-0" />
                          ) : (
                            <FiXCircle className="text-red-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-slate-700 truncate">
                            {res.name || res.teacherId}
                          </span>
                        </div>
                        {!res.success && (
                          <span className="text-red-500 text-[11px] ml-2 flex-shrink-0">
                            {res.message || "Failed"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                {successfulCount !== 0 && (
                  <button
                    onClick={onDownloadPDF}
                    disabled={isDownloading}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {isDownloading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    ) : (
                      <FiDownload className="w-4 h-4 mr-2" />
                    )}
                    {isDownloading ? "Preparing PDF..." : "Download PDF"}
                  </button>
                )}

                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkIDGenerationProgressModal;
